import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  StyledStuffContainer,
  StyledStuffImage,
  StyledStuffList,
  StyledNameBlock,
  StyledImgContainer,
  StyledStuffButton,
  StyledStuffWrapper,
  StyledOwnedToggle,
  StyledOwnedLabel,
  StyledHeader,
  StyledTitle,
  StyledButtonWrap,
  StyledEmpty,
} from './styled';
import SwitchToggle from '../SwitchToggle';
import { selectKitRequest } from '../../redux/actions/Stuff';
import { showPopupWindow } from '../../redux/actions/Popups';
import { POPUPS } from '../../service/constants';

const getKitFlags = (stuff) => {
  const availableForAll = stuff.type === 3;
  const forPurchase =
    (stuff.status === 0 && (stuff.type === 0 || stuff.type === 4)) ||
    (stuff.status === 2 && stuff.type === 1);
  const forSelect =
    stuff.status === 1 &&
    (stuff.type === 0 || stuff.type === 1 || stuff.type === 4);
  const forDisabled = stuff.status === 0 && stuff.type === 1;
  return { availableForAll, forPurchase, forSelect, forDisabled };
};

const isOwnedKit = (stuff, selectedKitId) => {
  const { forSelect, availableForAll } = getKitFlags(stuff);
  return forSelect || availableForAll || stuff._id === selectedKitId;
};

const TeamKits = ({ stuffData, selectedKit, isStuffLoading }) => {
  const dispatch = useDispatch();
  const [showOwnedOnly, setShowOwnedOnly] = useState(false);
  const selectedKitId = selectedKit && selectedKit._id;
  const visibleKits = useMemo(() => {
    const kits = stuffData || [];
    if (!showOwnedOnly) {
      return kits;
    }
    return kits.filter((stuff) => isOwnedKit(stuff, selectedKitId));
  }, [stuffData, showOwnedOnly, selectedKitId]);

  const kitSubmitType = (
    forPurchase,
    forSelect,
    forDisabled,
    id,
    availableForAll
  ) => {
    if (selectedKit && selectedKit._id === id) {
      return (
        <StyledStuffButton disabled={isStuffLoading} blue>
          Current
        </StyledStuffButton>
      );
    }
    if (forPurchase) {
      return (
        <StyledStuffButton
          disabled={isStuffLoading}
          yellow
          onClick={() =>
            dispatch(
              showPopupWindow(
                POPUPS.MOBILE_REQUIRED_POPUP,
                POPUPS.MOBILE_REQUIRED_POPUP
              )
            )
          }
        >
          Purchase
        </StyledStuffButton>
      );
    }
    if (forSelect || availableForAll) {
      return (
        <StyledStuffButton
          primary
          disabled={isStuffLoading}
          onClick={() => dispatch(selectKitRequest(id))}
        >
          Select
        </StyledStuffButton>
      );
    }
    if (forDisabled) {
      return (
        <StyledStuffButton blue disabled>
          Not available
        </StyledStuffButton>
      );
    }
    return null;
  };

  return (
    <>
      <StyledHeader>
        <StyledTitle>Kits</StyledTitle>
        <StyledOwnedToggle>
          <StyledOwnedLabel htmlFor="owned-kits">Owned</StyledOwnedLabel>
          <SwitchToggle
            id="owned-kits"
            toggled={showOwnedOnly}
            onChange={() => setShowOwnedOnly((current) => !current)}
          />
        </StyledOwnedToggle>
      </StyledHeader>
      {!isStuffLoading && visibleKits.length === 0 ? (
        <StyledEmpty>
          {showOwnedOnly ? 'No owned kits' : 'No kits available'}
        </StyledEmpty>
      ) : (
        <StyledStuffList>
          {visibleKits.map((stuff) => {
            const {
              availableForAll,
              forPurchase,
              forSelect,
              forDisabled,
            } = getKitFlags(stuff);
            const isCurrent = selectedKit && selectedKit._id === stuff._id;
            return (
              <StyledStuffWrapper
                key={stuff._id}
                disabled={forDisabled}
                $current={isCurrent}
              >
                <StyledStuffContainer>
                  <StyledImgContainer>
                    <StyledStuffImage src={stuff.image_1_link} alt="" />
                  </StyledImgContainer>
                  <StyledNameBlock>
                    <span>{stuff.name}</span>
                  </StyledNameBlock>
                  <StyledButtonWrap>
                    {kitSubmitType(
                      forPurchase,
                      forSelect,
                      forDisabled,
                      stuff._id,
                      availableForAll
                    )}
                  </StyledButtonWrap>
                </StyledStuffContainer>
              </StyledStuffWrapper>
            );
          })}
        </StyledStuffList>
      )}
    </>
  );
};
TeamKits.propTypes = {
  stuffData: PropTypes.array,
  selectedKit: PropTypes.shape,
  isStuffLoading: PropTypes.bool,
};
TeamKits.defaultProps = {
  stuffData: [],
  selectedKit: {},
  isStuffLoading: true,
};
export default TeamKits;
